import { safeInteropRegister, toGoLangInvoke } from "../../common/windowFunctions";
import { PromiseWrapper } from "../../utils/pw";
import { logStatus } from "../../utils";
export class Queries {
    constructor(gluePromise, asyncConstructors, loggerClient) {
        this.gluePromise = gluePromise;
        this.asyncConstructors = asyncConstructors;
        this.loggerClient = loggerClient;
        const pw = asyncConstructors.addConstructor();
        gluePromise.then((g) => {
            this.glue = g;
            asyncConstructors.finishConstructor(pw);
        });
    }
    logStatus(method, type, data) {
        logStatus(this.loggerClient, this.glue, {
            api: "Router.queries",
            method, type, data
        });
    }
    isInteropEndpointRegistered(channel) {
        return this.glue.interop.methods().some((m) => m.name === channel);
    }
    async addResponder(channel, queryCallback) {
        this.logStatus("addResponder", "start", { channel });
        await this.asyncConstructors.ready();
        if (this.isInteropEndpointRegistered(channel)) {
            const error = `addResponder failed because responder already added to ${channel}`;
            this.logStatus("addResponder", "error", error);
            throw (new Error(error));
        }
        await this.glue.interop.register(channel, safeInteropRegister(async (args, caller) => {
            const pw = new PromiseWrapper();
            const originatedHere = () => caller.windowId === (this.glue.windows.my()?.id ?? "Unknown or incompatible window type");
            const sendQueryResponse = (error, data) => {
                const isObject = typeof data === "object" && !Array.isArray(data);
                const sendData = isObject ? data : {
                    "#primitives-wrapper": data
                };
                pw.resolve({
                    err: error,
                    data: sendData
                });
            };
            const header = {
                origin: caller.instance ?? "Unknown origin",
                type: "query",
                queryID: "",
                channel,
                logWhenNoResponder: false,
                error: null,
                subscribeID: channel,
                lastClient: "",
                incomingTransportInfo: {
                    lastClient: "",
                    origin: caller.instance ?? "",
                    transportID: "FinsembleTransport",
                },
            };
            const finsembleFormattedResponse = {
                data: args?.["#primitives-wrapper"] ?? args,
                header,
                options: {
                    suppressWarnings: false
                },
                sendQueryResponse,
                originatedHere
            };
            queryCallback(null, finsembleFormattedResponse);
            this.logStatus("addResponder", "return");
            return await pw.promise;
        }));
    }
    removeResponder(responderChannel) {
        this.logStatus("removeResponder", "start", { responderChannel });
        this.glue.interop.unregister(responderChannel);
        this.logStatus("removeResponder", "return");
    }
    async query(channel, queryEvent, queryParams, responseEventHandler) {
        this.logStatus("query", "start", { channel });
        await this.asyncConstructors.ready();
        const cb = typeof queryParams === "function" ? queryParams : responseEventHandler;
        const isObject = typeof queryEvent === "object" && !Array.isArray(queryEvent);
        const sendData = isObject ? queryEvent : {
            "#primitives-wrapper": queryEvent
        };
        const requestedTimeout = queryParams?.timeout;
        const invokeOptions = requestedTimeout ? {
            methodResponseTimeoutMs: requestedTimeout
        } : undefined;
        const header = {
            channel,
            topic: channel,
            type: "",
            origin: "",
            subscribeID: channel,
            lastClient: "",
            incomingTransportInfo: {
                lastClient: "",
                origin: "",
                transportID: "FinsembleTransport",
            },
            originMetadata: {
                routerConnectionId: "",
                transportInfo: {
                    transportID: "FinsembleTransport"
                },
                windowName: "",
                appId: "",
                webContentsName: ""
            }
        };
        if (!this.isInteropEndpointRegistered(channel)) {
            const error = `query failed since no responder on ${channel}`;
            this.logStatus("query", "error", error);
            cb?.(error, { header });
            return { err: error, response: { header } };
        }
        const { err, data } = await toGoLangInvoke(() => this.glue.interop.invoke(channel, sendData, undefined, invokeOptions));
        const unwrappedData = data?.["#primitives-wrapper"] ?? data;
        const response = {
            data: unwrappedData,
            header
        };
        this.logStatus("query", "return");
        cb?.(err, response);
        return { err, response };
    }
}
